import React from "react";
import Link from "next/link";
import StaticContainer from "../layouts/StaticContainer";
import Meta from "../layouts/Meta";
import styles from "../styles/pages/faq.module.scss";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BsChatLeftText } from "react-icons/bs";
import { useLiveTranslation } from "../lib/LiveContext";
import { stat, statNum, ImpactStatsMap } from "../constants/impact_stats";
import { cmsPageProps } from "../lib/pageProps";
import { GetStaticProps } from "next";
import { getImpactStatsMap, getPublishedStats } from "../services/cms/impact";
import { IMPACT_STATS_SNAPSHOT } from "../constants/generated/impact_stats";
import { figuresAsOfLabel } from "../lib/figuresAsOf";
import { linkifyParagraph } from "../lib/faqLinkify";

// Section -> ordered question keys. Each key has `${key}-q` and `${key}-a` in the
// locale files. Both the visible page and the FAQPage JSON-LD are built from this
// single list, so the schema can never drift from what is on the page.
const FAQ_SECTIONS: { titleKey: string; questions: string[] }[] = [
  {
    titleKey: "section-about",
    questions: ["q-what-is", "q-registered", "q-designed-for", "q-accredited"],
  },
  {
    titleKey: "section-eligibility",
    questions: ["q-free", "q-b40", "q-qualifications"],
  },
  {
    titleKey: "section-programmes",
    questions: ["q-programmes", "q-skills-hub-free", "q-sawa"],
  },
  {
    titleKey: "section-solar-academy",
    questions: [
      "q-solar-academy",
      "q-solar-duration",
      "q-solar-apply",
      "q-solar-outcomes",
    ],
  },
  {
    titleKey: "section-energy-access",
    questions: ["q-energy-access", "q-decarbonise", "q-energy-support"],
  },
  {
    titleKey: "section-certificates",
    questions: ["q-certificate"],
  },
];

// Split a long answer into at most two paragraphs at a sentence boundary so
// answers don't render as one heavy block. CJK sentences end with 。 (no space);
// Latin sentences end with . ! ? followed by whitespace (so emails, URLs and
// decimals are never split).
function toParagraphs(text: string): string[] {
  const isCJK = /[一-鿿]/.test(text);
  const parts = text
    .split(/(?<=。)|(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 3) return [text.trim()];
  const sep = isCJK ? "" : " ";
  const mid = Math.ceil(parts.length / 2);
  return [parts.slice(0, mid).join(sep), parts.slice(mid).join(sep)];
}

// The figures each "as of" quarter in the answers stands behind.
const ACADEMY_KEYS = [
  "solar_academy_graduates",
  "solar_academy_employment_rate",
  "solar_academy_income_increase",
];
const ENERGY_ACCESS_KEYS = ["energy_access_homes"];

const Faq = ({
  impact,
  academyAsOf,
  energyAccessAsOf,
}: {
  impact: ImpactStatsMap;
  academyAsOf: string;
  energyAccessAsOf: string;
}) => {
  const { t, i18n } = useLiveTranslation("faq");
  // Impact figures from the CMS rows read for this page (services/cms/impact.ts,
  // over the fallbacks in constants/impact_stats.ts). `families` keeps its "+"
  // and the rates carry their "%" suffix (formatted via stat); `lives` and
  // `graduates` are bare numbers because the copy supplies the noun.
  const stats = {
    families: stat("energy_access_homes", i18n.language, impact),
    lives: statNum("lives_transformed", i18n.language, impact),
    graduates: statNum("solar_academy_graduates", i18n.language, impact),
    employmentRate: stat("solar_academy_employment_rate", i18n.language, impact),
    incomeIncrease: stat("solar_academy_income_increase", i18n.language, impact),
    academyAsOf,
    energyAccessAsOf,
  };
  // The founding years, the years of service, the email and the registration
  // number in the answer copy come from the live values `t` adds
  // (lib/LiveContext.tsx); these figures, passed explicitly, take precedence.

  // Schema uses the raw, unmodified answer text (plain, single string per Q).
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_SECTIONS.flatMap((s) => s.questions).map((key) => ({
      "@type": "Question",
      name: t(`${key}-q`),
      acceptedAnswer: { "@type": "Answer", text: t(`${key}-a`, stats) },
    })),
  };

  return (
    <StaticContainer title={t("title")}>
      <Meta
        title={t("title")}
        description={t("meta-description")}
        keywords={t("meta-keyword")}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema).replace(/</g, "\\u003c"),
        }}
      />

      <div className={styles.faqLayout}>
        <div className={styles.faqMain}>
        {FAQ_SECTIONS.map((section) => (
          <section key={section.titleKey} className={styles.faqSection}>
            <h2 className={styles.faqSectionTitle}>{t(section.titleKey)}</h2>
            {section.questions.map((key) => {
              const linked = new Set<string>();
              return (
                <Accordion
                  key={key}
                  elevation={0}
                  className={styles.accordionItem}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon className={styles.accordionIcon} />
                    }
                    aria-controls={`${key}-content`}
                    id={`${key}-header`}
                    className={styles.accordionHeading}
                  >
                    <h3>{t(`${key}-q`)}</h3>
                  </AccordionSummary>
                  <AccordionDetails className={styles.accordionDetails}>
                    {toParagraphs(t(`${key}-a`, stats)).map((para, i) => (
                      <p
                        key={i}
                        // Content is our own copy; links are injected by linkifyParagraph
                        // and the text is HTML-escaped first.
                        dangerouslySetInnerHTML={{
                          __html: linkifyParagraph(para, linked),
                        }}
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </section>
        ))}
        </div>
        <aside className={styles.faqAside}>
          <div className={styles.ctaBox}>
            <div className={styles.ctaHeader}>
              <BsChatLeftText className={styles.ctaIcon} />
              <h3 className={styles.ctaHeading}>{t("cta-heading")}</h3>
            </div>
            <p className={styles.ctaText}>{t("cta-text")}</p>
            <Link href="/contact-us" className={styles.ctaButton}>
              {t("cta-button")}
            </Link>
          </div>
        </aside>
      </div>
    </StaticContainer>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  // Started together so the page and its props share one read of each.
  const [impact, rows, pageProps] = await Promise.all([
    getImpactStatsMap(),
    getPublishedStats(),
    cmsPageProps(locale, ["faq"]),
  ]);
  // The snapshot carries a date for every key, so the quarter always fills;
  // it also backs the live rows when one of them has no date.
  const asOf = (keys: string[]) =>
    figuresAsOfLabel(rows, IMPACT_STATS_SNAPSHOT, keys, locale) ??
    figuresAsOfLabel(IMPACT_STATS_SNAPSHOT, IMPACT_STATS_SNAPSHOT, keys, locale) ??
    "";

  return {
    props: {
      impact,
      academyAsOf: asOf(ACADEMY_KEYS),
      energyAccessAsOf: asOf(ENERGY_ACCESS_KEYS),
      ...pageProps,
    },
    revalidate: 60,
  };
};

export default Faq;
